import React, { useState, useEffect } from 'react';
import { getSettings, saveSettings } from '../utils/api';
import { Toggle } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

export default function AutoResponse({ toast }) {
  const { can } = useAuth();
  const { branding } = useBranding();
  const isAdmin = can('autoresponse.manage');
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings().then(r => setSettings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const save = async () => {
    if (!isAdmin) {
      toast('You do not have permission to edit auto-response settings');
      return;
    }
    setSaving(true);
    try { await saveSettings(settings); toast('Settings saved!'); } catch { toast('Save failed'); } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--gray-400)' }}>Loading...</div>;

  const apiBase = process.env.REACT_APP_API_URL || '/api';
  const cronUrl = `${window.location.origin}${apiBase}/cron-followup?secret=YOUR_CRON_SECRET`;

  return (
    <div>
      <div className="section-header">
        <div className="section-title">Auto-Response Templates</div>
        {isAdmin ? (
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save All'}</button>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>Read-only for your account</span>
        )}
      </div>

      {/* New Lead Auto-Email */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <div className="card-title"><span><i className="ti ti-brand-whatsapp" style={{ color: '#25d366', marginRight: 6 }} />WhatsApp Template</span></div>
          <textarea className="form-input" rows={8} value={settings.whatsapp_template || ''} onChange={e => set('whatsapp_template', e.target.value)} />
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'var(--amber-light)', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
            <i className="ti ti-info-circle" style={{ marginRight: 4 }} />WhatsApp Business API key required. Plug-in ready when you get one.
          </div>
        </div>
        <div className="card">
          <div className="card-title"><span><i className="ti ti-mail" style={{ color: 'var(--primary)', marginRight: 6 }} />New Lead Email Template</span></div>
          <div className="form-group">
            <label className="form-label">Subject</label>
            <input className="form-input" value={settings.email_subject || ''} onChange={e => set('email_subject', e.target.value)} placeholder={`Thank you for contacting ${branding.brand_name}`} />
          </div>
          <div className="form-group">
            <label className="form-label">Body — use <code>{'{{name}}'}</code> and <code>{'{{service}}'}</code></label>
            <textarea className="form-input" rows={7} value={settings.email_body || ''} onChange={e => set('email_body', e.target.value)} />
          </div>
          <div style={{ padding: '8px 12px', background: 'var(--primary-light)', borderRadius: 8, fontSize: 12, color: '#1e40af' }}>
            <i className="ti ti-mail-forward" style={{ marginRight: 4 }} />Connected to {settings.gmail_from || 'your configured sender address'}. Configure outgoing email (platform, Gmail, or your own SMTP) in Settings.
          </div>
        </div>
      </div>

      {/* Not Connected Follow-up Email */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">
          <span><i className="ti ti-phone-off" style={{ color: 'var(--primary)', marginRight: 6 }} />📵 Not Connected — Day 2 Follow-up Email</span>
          <Toggle value={settings.nc_followup_enabled === '1'} onChange={v => set('nc_followup_enabled', v ? '1' : '0')} />
        </div>

        <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 16 }}>
          When a lead's status is set to <strong>Not Connected</strong>, a follow-up email is automatically sent on the 2nd day via cron job.
          This email is separate from the new-lead auto-response.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Subject — use <code>{'{{name}}'}</code>, <code>{'{{service}}'}</code></label>
            <input
              className="form-input"
              value={settings.nc_followup_subject || ''}
              onChange={e => set('nc_followup_subject', e.target.value)}
              placeholder="We tried reaching you — {{name}}"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '0.5px solid #bbf7d0', borderRadius: 8, fontSize: 12, color: '#166534', flex: 1 }}>
              <i className="ti ti-clock" style={{ marginRight: 6 }} />
              Email fires <strong>2 days</strong> after a lead is marked Not Connected, only if they have an email address and follow-up hasn't been sent yet.
            </div>
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Body — use <code>{'{{name}}'}</code>, <code>{'{{service}}'}</code></label>
          <textarea
            className="form-input"
            rows={6}
            value={settings.nc_followup_body || ''}
            onChange={e => set('nc_followup_body', e.target.value)}
            placeholder={`Dear {{name}},\n\nWe tried calling you regarding your enquiry about {{service}} but couldn't connect.\n\nPlease reply or call us at a convenient time.\n\nWarm regards,\nTeam ${branding.brand_name}`}
          />
        </div>

        {/* Cron setup instructions */}
        {/* <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--gray-50)', border: '0.5px solid var(--gray-200)', borderRadius: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--gray-600)' }}>
            <i className="ti ti-terminal" style={{ marginRight: 6 }} />Cron Job Setup (run once daily)
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 8 }}>
            Add this to your server's crontab (<code>crontab -e</code>). Set <code>CRON_SECRET</code> in your <code>.env</code> file first.
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 11, background: 'var(--black)', color: '#86efac', padding: '10px 14px', borderRadius: 6, wordBreak: 'break-all' }}>
            0 9 * * * curl -s "{cronUrl}"
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 8 }}>
            Runs every day at 9 AM. You can also trigger it manually by visiting the URL above in your browser.
          </div>
        </div> */}
      </div>

      {/* Trigger Rules */}
      <div className="card">
        <div className="card-title">Trigger Rules</div>
        {[
          { key: 'auto_email_enabled',       label: 'Send email on new lead',             sub: 'Auto-email to lead\'s email address when a new lead arrives' },
          { key: 'auto_whatsapp_enabled',    label: 'Send WhatsApp on new lead',           sub: 'Requires WhatsApp Business API key' },
          { key: 'followup_digest_enabled',  label: 'Daily follow-up digest email',        sub: 'Morning email with today\'s follow-up tasks' },
          { key: 'nc_followup_enabled',      label: '📵 Not Connected day-2 follow-up',    sub: 'Automated email 2 days after lead is marked Not Connected' },
        ].map(({ key, label, sub }) => (
          <div key={key} className="settings-row">
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{sub}</div>
            </div>
            <Toggle value={settings[key] === '1'} onChange={v => set(key, v ? '1' : '0')} />
          </div>
        ))}
      </div>
    </div>
  );
}
